import React, { useState, useEffect, useCallback, useMemo } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import {
  AnchorModel,
  AnchorType,
  ShareModel,
  IDate,
  ITagAssociationMeta,
  IEntityMemoryMeta,
  IEntityMediaMeta,
  IEntityContactMeta,
  IEntityGroupMeta,
  IEntityPlaceMeta,
  IEntityAnchorMeta,
  formatDisplayDate,
} from "common247";
import { ThemedText } from "../ThemedText";
import { ThemedInput } from "../ThemedInput";
import { ThemedButton } from "../ThemedButton";
import { ThemedView } from "../ThemedView";
import { ThemedRating } from "../ThemedRating";
import { SharingControl } from "../sharing/SharingControl";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useActiveFieldTracker } from "@/hooks/useActiveField";
import { AutoSaveIndicator } from "@/components/ui/AutoSaveIndicator";
import { DateSelector } from "@/components/date/DateSelector";
import { TagListStrip } from "../tags/TagListStrip";
import { EntityLinkStrip } from "../ui/EntityLinkStrip";
import { CollaborativeEditor } from "../editor/CollaborativeEditor";
import { GoogleMapsLocationSelector } from "../location/GoogleMapsLocationSelector";
import { useFirebase } from "../firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useColorScheme } from "@/hooks/useColorScheme";
import { getChipVisualConfig } from "@/constants/themes/components/chip-theme";
import { CustomAlert } from "@/components/ThemedAlert";

const ANCHOR_TYPES: { type: AnchorType; label: string; icon: keyof typeof Ionicons.glyphMap }[] = [
  { type: "animals", label: "Animals", icon: "paw-outline" },
  { type: "hobbies", label: "Hobbies", icon: "color-palette-outline" },
  { type: "courses", label: "Courses", icon: "school-outline" },
  { type: "other", label: "Other", icon: "cube-outline" },
];

/**
 * Props for the AnchorEditor component.
 */
export interface IAnchorEditorProps {
  /** Existing anchor model if editing */
  anchor?: AnchorModel;
  /** Existing shares */
  shares?: ShareModel[];
  /** Loading shares flag */
  isLoadingShares?: boolean;
  /** Current authenticated user ID */
  currentUserId?: string;
  /** Save callback */
  onSave: (data: {
    name: string;
    type: AnchorType;
    description?: string;
    date?: IDate;
    address?: string;
    latitude?: number;
    longitude?: number;
    favouriteRating?: number;
    photoMediaId?: string;
    tags?: Record<string, ITagAssociationMeta>;
    memories?: Record<string, IEntityMemoryMeta>;
    media?: Record<string, IEntityMediaMeta>;
    contacts?: Record<string, IEntityContactMeta>;
    groups?: Record<string, IEntityGroupMeta>;
    places?: Record<string, IEntityPlaceMeta>;
    anchors?: Record<string, IEntityAnchorMeta>;
  }) => Promise<void> | void;
  /** Share callback */
  onShare?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  /** Unshare callback */
  onUnshare?: (shareId: string) => void;
  /** Cancel callback */
  onCancel: () => void;
  /** Delete callback */
  onDelete?: () => Promise<void> | void;
  /** Saving in-flight flag */
  isSaving: boolean;
  /** Submit button text */
  submitTitle?: string;
  /** Optional child elements */
  children?: React.ReactNode;
}

/**
 * First-class AnchorEditor component for creating and modifying Anchors.
 * Standardizes Section 20 Editor Standards:
 * - Name input (fail-fast validation)
 * - Lowercase Anchor type selector (animals, hobbies, courses, other)
 * - Address & Coordinates with GoogleMapsLocationSelector
 * - Structured Date with DateSelector
 * - Rich description with CollaborativeEditor
 * - Rating with ThemedRating
 * - Cross-entity reciprocal linking (Memories, Media, Contacts, Groups, Places, Child Anchors)
 * - Tagging with TagListStrip
 * - Sharing control with SharingControl
 * - Auto-save integration with AutoSaveIndicator
 */
export function AnchorEditor({
  anchor,
  shares = [],
  isLoadingShares,
  currentUserId,
  onSave,
  onShare,
  onUnshare,
  onCancel,
  onDelete,
  isSaving,
  submitTitle = "Save Anchor",
  children,
}: IAnchorEditorProps) {
  const [formName, setFormName] = useState(anchor?.name || "");
  const [formType, setFormType] = useState<AnchorType>(anchor?.type || "other");
  const [formDesc, setFormDesc] = useState(anchor?.description || "");
  const [formDate, setFormDate] = useState<IDate | undefined>(anchor?.date);
  const [formAddress, setFormAddress] = useState(anchor?.address || "");
  const [formLat, setFormLat] = useState<number | undefined>(anchor?.latitude);
  const [formLng, setFormLng] = useState<number | undefined>(anchor?.longitude);
  const [favouriteRating, setFavouriteRating] = useState<number>(anchor?.favouriteRating ?? 3);
  const [nameError, setNameError] = useState("");

  // Modals
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // Cross-entity links
  const [memoriesMap, setMemoriesMap] = useState<Record<string, IEntityMemoryMeta>>(anchor?.memories || {});
  const [mediaMap, setMediaMap] = useState<Record<string, IEntityMediaMeta>>(anchor?.media || {});
  const [contactsMap, setContactsMap] = useState<Record<string, IEntityContactMeta>>(anchor?.contacts || {});
  const [groupsMap, setGroupsMap] = useState<Record<string, IEntityGroupMeta>>(anchor?.groups || {});
  const [placesMap, setPlacesMap] = useState<Record<string, IEntityPlaceMeta>>(anchor?.places || {});
  const [anchorsMap, setAnchorsMap] = useState<Record<string, IEntityAnchorMeta>>(anchor?.anchors || {});
  const [tagsMap, setTagsMap] = useState<Record<string, ITagAssociationMeta>>(anchor?.tags || {});

  const services = useFirebase();
  const resolvedUserId = currentUserId || services?.auth?.currentUser?.uid || "";
  const userRole = anchor?.userRole || "owner";
  const isOwner = userRole === "owner";
  const canEdit = isOwner || userRole === "editor";

  const { activeField, setActiveField, isFieldActive } = useActiveFieldTracker();

  // Reset fields if anchor changes
  useEffect(() => {
    if (anchor) {
      setFormName(anchor.name || "");
      setFormType(anchor.type || "other");
      setFormDesc(anchor.description || "");
      setFormDate(anchor.date);
      setFormAddress(anchor.address || "");
      setFormLat(anchor.latitude);
      setFormLng(anchor.longitude);
      setFavouriteRating(anchor.favouriteRating ?? 3);
      setMemoriesMap(anchor.memories || {});
      setMediaMap(anchor.media || {});
      setContactsMap(anchor.contacts || {});
      setGroupsMap(anchor.groups || {});
      setPlacesMap(anchor.places || {});
      setAnchorsMap(anchor.anchors || {});
      setTagsMap(anchor.tags || {});
    }
  }, [anchor]);

  const handleManualSave = useCallback(async () => {
    const trimmedName = (formName || "").trim();
    if (!trimmedName) {
      setNameError("Anchor name is required.");
      return;
    }
    setNameError("");

    await onSave({
      name: trimmedName,
      type: formType,
      description: (formDesc || "").trim() || undefined,
      date: formDate,
      address: (formAddress || "").trim() || undefined,
      latitude: formLat,
      longitude: formLng,
      favouriteRating,
      tags: tagsMap,
      memories: memoriesMap,
      media: mediaMap,
      contacts: contactsMap,
      groups: groupsMap,
      places: placesMap,
      anchors: anchorsMap,
    });
  }, [
    formName,
    formType,
    formDesc,
    formDate,
    formAddress,
    formLat,
    formLng,
    favouriteRating,
    tagsMap,
    memoriesMap,
    mediaMap,
    contactsMap,
    groupsMap,
    placesMap,
    anchorsMap,
    onSave,
  ]);

  // Auto-save setup
  const currentFormState = useMemo(
    () => ({
      name: formName,
      type: formType,
      description: formDesc,
      date: formDate,
      address: formAddress,
      latitude: formLat,
      longitude: formLng,
      favouriteRating,
      tags: tagsMap,
      memories: memoriesMap,
      media: mediaMap,
      contacts: contactsMap,
      groups: groupsMap,
      places: placesMap,
      anchors: anchorsMap,
    }),
    [
      formName,
      formType,
      formDesc,
      formDate,
      formAddress,
      formLat,
      formLng,
      favouriteRating,
      tagsMap,
      memoriesMap,
      mediaMap,
      contactsMap,
      groupsMap,
      placesMap,
      anchorsMap,
    ]
  );

  const isExisting = !!anchor?.id;

  const autoSave = useAutoSave({
    data: currentFormState,
    enabled: isExisting && canEdit && !!(formName || "").trim(),
    debounceMs: 600,
    onSave: async (state) => {
      const trimmedName = (state.name || "").trim();
      if (!anchor?.id || !trimmedName) return;
      await onSave({
        name: trimmedName,
        type: state.type,
        description: (state.description || "").trim() || undefined,
        date: state.date,
        address: (state.address || "").trim() || undefined,
        latitude: state.latitude,
        longitude: state.longitude,
        favouriteRating: state.favouriteRating,
        tags: state.tags,
        memories: state.memories,
        media: state.media,
        contacts: state.contacts,
        groups: state.groups,
        places: state.places,
        anchors: state.anchors,
      });
    },
  });

  const isDark = useColorScheme() === "dark";
  const anchorVisual = getChipVisualConfig("anchor", isDark);
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({ light: "#64748B", dark: "#94A3B8" }, "text");
  const borderColor = useThemeColor({}, "border");

  return (
    <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.contentContainer}>
      {/* Details Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>
          Anchor Details
        </ThemedText>

        <View style={styles.fieldBlock}>
          <ThemedText type="fieldLabel">Anchor Name *</ThemedText>
          <ThemedInput
            value={formName}
            onChangeText={(val) => {
              setFormName(val);
              if (nameError) setNameError("");
            }}
            placeholder="e.g. Bella (Pet), Piano Lessons, Biology 101"
            error={nameError}
            onFocus={() => setActiveField("name")}
            onBlur={() => setActiveField(null)}
            editable={canEdit}
            testID="anchor-name-input"
          />
        </View>

        {/* Categorical Anchor Type Selection */}
        <View style={styles.fieldBlock}>
          <ThemedText type="fieldLabel">Type *</ThemedText>
          <View style={styles.typeSelectorRow}>
            {ANCHOR_TYPES.map((t) => {
              const isSelected = formType === t.type;
              return (
                <TouchableOpacity
                  key={t.type}
                  style={[
                    styles.typeChip,
                    { borderColor: isSelected ? anchorVisual.borderColor : borderColor },
                    isSelected && { backgroundColor: anchorVisual.bgColor },
                  ]}
                  onPress={() => canEdit && setFormType(t.type)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isSelected }}
                  accessibilityLabel={`Select ${t.label} type`}
                  testID={`anchor-type-${t.type}`}
                >
                  <Ionicons
                    name={t.icon}
                    size={15}
                    color={isSelected ? anchorVisual.textColor : textMuted}
                    style={{ marginRight: 6 }}
                  />
                  <ThemedText
                    style={[
                      styles.typeChipText,
                      { color: isSelected ? anchorVisual.textColor : textMuted },
                      isSelected && { fontWeight: "600" },
                    ]}
                  >
                    {t.label}
                  </ThemedText>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Address and Map Coordinates */}
        <View style={styles.fieldBlock}>
          <ThemedText type="fieldLabel">Address / Location</ThemedText>
          <View style={styles.addressRow}>
            <View style={{ flex: 1 }}>
              <ThemedInput
                value={formAddress}
                onChangeText={setFormAddress}
                placeholder="e.g. Veterinary Clinic, Music Academy"
                onFocus={() => setActiveField("address")}
                onBlur={() => setActiveField(null)}
                editable={canEdit}
                testID="anchor-address-input"
              />
            </View>

            <TouchableOpacity
              style={[styles.mapPickerButton, { borderColor: tintColor }]}
              onPress={() => setShowLocationPicker(true)}
              accessibilityLabel="Pick location on map"
            >
              <Ionicons name="map-outline" size={18} color={tintColor} />
              <ThemedText style={[styles.mapPickerText, { color: tintColor }]}>
                Map
              </ThemedText>
            </TouchableOpacity>
          </View>
        </View>

        {formLat !== undefined && formLng !== undefined && (
          <ThemedText style={[styles.coordinatesBadge, { color: textMuted }]}>
            Coordinates: {formLat.toFixed(4)}, {formLng.toFixed(4)}
          </ThemedText>
        )}

        {/* Structured Date */}
        <View style={styles.fieldBlock}>
          <ThemedText type="fieldLabel">Date</ThemedText>
          <DateSelector
            date={formDate}
            onChangeDate={setFormDate}
            editable={canEdit}
          />
        </View>

        {/* Rating */}
        <View style={styles.ratingRow}>
          <ThemedText type="fieldLabel">Favourite Rating</ThemedText>
          <ThemedRating
            value={favouriteRating}
            onChange={setFavouriteRating}
            readOnly={!canEdit}
            size={18}
            showLabel={false}
            style={styles.ratingControl}
          />
        </View>
      </ThemedView>

      {/* Description Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>
          Description
        </ThemedText>
        <CollaborativeEditor
          entityId={anchor?.id}
          entityType="anchor"
          value={formDesc}
          onChange={setFormDesc}
          placeholder="Add details, milestones, or notes about this anchor... Use @ for contacts/groups, # for memories, and + for media."
          editable={canEdit}
        />
      </ThemedView>

      {/* 1. Linked Contacts Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Contacts</ThemedText>
        <EntityLinkStrip
          entityId={anchor?.id}
          entityType="anchor"
          targetType="contact"
          links={contactsMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setContactsMap}
        />
      </ThemedView>

      {/* 2. Linked Groups Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Groups</ThemedText>
        <EntityLinkStrip
          entityId={anchor?.id}
          entityType="anchor"
          targetType="group"
          links={groupsMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setGroupsMap}
        />
      </ThemedView>

      {/* 3. Linked Places Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Places</ThemedText>
        <EntityLinkStrip
          entityId={anchor?.id}
          entityType="anchor"
          targetType="place"
          links={placesMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setPlacesMap}
        />
      </ThemedView>

      {/* 4. Linked Child / Related Anchors Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Anchors</ThemedText>
        <EntityLinkStrip
          entityId={anchor?.id}
          entityType="anchor"
          targetType="anchor"
          links={anchorsMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setAnchorsMap}
        />
      </ThemedView>

      {/* 5. Linked Media Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Media</ThemedText>
        <EntityLinkStrip
          entityId={anchor?.id}
          entityType="anchor"
          targetType="media"
          links={mediaMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setMediaMap}
        />
      </ThemedView>

      {/* 6. Linked Memories Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Memories</ThemedText>
        <EntityLinkStrip
          entityId={anchor?.id}
          entityType="anchor"
          targetType="memory"
          links={memoriesMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onLinksChange={setMemoriesMap}
        />
      </ThemedView>

      {/* 7. Tags Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Tags & Categories</ThemedText>
        <TagListStrip
          entityId={anchor?.id}
          entityType="anchor"
          tags={tagsMap}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
          onTagsChange={setTagsMap}
        />
      </ThemedView>

      {/* 8. Sharing Control */}
      {anchor?.id && (
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Shares</ThemedText>
          <SharingControl
            sourceId={anchor.id}
            sourceType="anchor"
            shares={shares}
            isLoading={isLoadingShares}
            canShare={anchor.canShare}
            onShare={onShare}
            onUnshare={onUnshare}
          />
        </ThemedView>
      )}

      {children}

      {/* Standard Item Editor Footer: Back/Cancel on left, AutoSaveIndicator to its right, and action buttons on right */}
      <View style={styles.footer}>
        <View style={styles.leftFooterControls}>
          <ThemedButton
            title={isExisting || !canEdit ? "Back" : "Cancel"}
            variant="minimal"
            onPress={onCancel}
            style={styles.actionBtn}
            testID="anchor-cancel-button"
          />
          {isExisting && (
            <AutoSaveIndicator
              status={autoSave.status}
              errorMessage={autoSave.error}
            />
          )}
        </View>

        <View style={styles.rightFooterControls}>
          {isExisting && isOwner && onDelete && (
            <ThemedButton
              title="Delete Anchor"
              variant="destructive"
              onPress={() => {
                CustomAlert.alert(
                  "Delete Anchor",
                  "Are you sure you want to permanently delete this anchor?",
                  [
                    { text: "Cancel", style: "cancel" },
                    {
                      text: "Delete",
                      style: "destructive",
                      onPress: async () => {
                        await onDelete();
                      },
                    },
                  ]
                );
              }}
              style={styles.actionBtn}
              testID="anchor-delete-button"
            />
          )}

          {!isExisting && canEdit && (
            <ThemedButton
              title={isSaving ? "Creating..." : (submitTitle || "Create Anchor")}
              onPress={handleManualSave}
              disabled={isSaving}
              loading={isSaving}
              style={styles.actionBtn}
              testID="anchor-submit-button"
            />
          )}
        </View>
      </View>

      {/* Map Location Selector Modal */}
      {showLocationPicker && (
        <GoogleMapsLocationSelector
          visible={showLocationPicker}
          onClose={() => setShowLocationPicker(false)}
          initialLocation={formAddress}
          initialLatitude={formLat}
          initialLongitude={formLng}
          onSelect={(result) => {
            setFormAddress(result.location);
            setFormLat(result.latitude);
            setFormLng(result.longitude);
            setShowLocationPicker(false);
          }}
        />
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 40,
  },
  section: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.05)",
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    marginBottom: 16,
    gap: 12,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 4,
  },
  fieldBlock: {
    gap: 6,
  },
  typeSelectorRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  typeChip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  typeChipText: {
    fontSize: 13,
  },
  addressRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  mapPickerButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    gap: 6,
  },
  mapPickerText: {
    fontSize: 13,
    fontWeight: "600",
  },
  coordinatesBadge: {
    fontSize: 12,
    marginTop: -6,
  },
  footer: {
    marginTop: 8,
    paddingBottom: 24,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  leftFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  rightFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  actionBtn: {
    minWidth: 140,
  },
  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    marginTop: 12,
    marginBottom: 6,
    gap: 12,
  },
  ratingLabel: {
    marginBottom: 0,
  },
  ratingControl: {
    marginVertical: 0,
  },
});
